import { ContactStatus } from '@wonderschool/common-base-types';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  CloseIcon,
  showToast,
  TextArea,
  Toast,
  ToastTypeEnum,
} from '@wonderschool/common-base-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Form, Segment } from 'semantic-ui-react';
import LoadingComponent from '../../../Components/Shared/LoadingComponent';
import PageHeader from '../../../Components/Shared/PageHeader';
import { sendMail } from '../../../api/firebase/mail';
import { INVITATION_TYPES } from '../../../contacts';
import { inviteContact } from '../../../contacts/contactsAPI';
import { useContacts } from '../../../contacts/contactsHooks';
import { useOrganization } from '../../../hooks/useOrganizations';
import { useUser } from '../../../hooks/useUser';
import { RouteNameEnum, usePaths, useRoutes } from '../../../navigation';
import { newOrganizationInvoice, saveOrganizationInvoicePlan } from '../../../redux/actions/invoiceActions';
import { logError } from '../../../rollbar';
import { useStudent } from '../../../students/studentsHooks';
import { useDefaultBusinessHours } from '../../enrollmentsHooks';
import { getDocuments, getInvitationRecipients, getScheduledDays, getTuitionAndFees } from '../../enrollmentsUtils';
import { convertDataToInvoice, convertDataToPlan, convertDataToProrateInvoice } from '../../invoiceUtils';
import { DocumentType, InvitationRecipientType, ScheduledDayType, TuitionAndFeesType } from '../../types';
import { DocumentsView, InvitationMessageView, InvitationRecipientsView, ScheduleView, StudentView } from '../forms';
import EnrollmentInvoicesView from '../forms/EnrollmentInvoicesView';
import { EnrollmentInvitationConfirmationModal } from '../modals';

interface TuitionWithDetails {
  detailedTuition?: { planEndDate?: string };
}

const parseValidDate = (dateStr?: string): Date | null => {
  if (!dateStr || !dateStr.trim()) {
    return null;
  }

  const date = new Date(dateStr.trim());

  if (isNaN(date.getTime())) {
    console.warn(`Invalid date string: "${dateStr}"`);
    return null;
  }

  return date;
};

type EnrollmentsPageProps = {
  onClick?: () => void;
};

const EnrollmentPage: React.FC<EnrollmentsPageProps> = () => {
  const { t } = useTranslation();
  const params: any = useParams();
  const organization = useOrganization();
  const loggedInUser = useUser();

  const defaultBusinessHours = useDefaultBusinessHours();
  const student = useStudent(params.studentId);
  const { goBack, gotoRouteByName } = useRoutes();
  const { paths } = usePaths();

  const { familyContacts, directorContact } = useContacts();
  const dispatch = useDispatch();

  const invitationRecipients: InvitationRecipientType[] = useMemo(() => getInvitationRecipients(student), [student]);
  const documents: DocumentType[] = useMemo(() => getDocuments(student), [student]);
  const tuitionAndFees: TuitionAndFeesType = useMemo(() => getTuitionAndFees(student), [student]);
  const scheduledDays: ScheduledDayType[] = useMemo(
    () => getScheduledDays(student, defaultBusinessHours),
    [student, defaultBusinessHours]
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [extraNotes, setExtraNotes] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [_errors, setErrors] = useState<string[]>([]);

  const sendEnrollmentInvitation = useCallback(
    async (contact: any) => {
      try {
        contact.invitationType = INVITATION_TYPES.enrollment;
        contact.invitationNotes = extraNotes;
        await inviteContact(contact);
      } catch (error: any) {
        logError('Error saving contact', contact, error);
        setErrors((prev) => [...prev, error.message]);
      }
    },
    [extraNotes]
  );

  const sendLoginEmail = useCallback(
    async (contact: any) => {
      if (!contact?.email) return;

      try {
        const mailData = {
          to: contact.email,
          template: {
            name: 'enrollment-parent-share-docs',
            data: {
              subject: "Please login to view your child's documents",
              parentName: contact.firstName,
              directorName: directorContact?.displayName,
              organizationName: organization.name,
              documentsLink: `${window.location.origin}${paths.documents}`, // paths.documents starts with a `/`
              notes: extraNotes || '-',
            },
          },
        };
        return sendMail(mailData);
      } catch (error: any) {
        logError('Error saving contact', contact, error);
        setErrors((prev) => [...prev, error.message]);
      }
    },
    [directorContact?.displayName, extraNotes, organization.name, paths.documents]
  );

  // iterate throught the recipients and set state in the contact to resend invitations, but using the
  // enrollment email template. If the contact is verified, instead of sending an invitation, we will
  // send an email to login and view their documents
  const sendEnrollmentInvitations = useCallback(async () => {
    for (const recipient of invitationRecipients) {
      const contact = familyContacts.find((c: any) => c.id === recipient.contactId);
      // If the contact is new or invited, we can re-send the invitation, but with an invitation type of enrollment
      // and with the extra notes
      if (contact?.status === ContactStatus.new || contact?.status === ContactStatus.invited) {
        await sendEnrollmentInvitation(contact);
      } else if (contact?.status === ContactStatus.verified) {
        await sendLoginEmail(contact);
      }
      setIsModalOpen(true);
    }
  }, [familyContacts, invitationRecipients, sendEnrollmentInvitation, sendLoginEmail]);

  const sendInvoices = async () => {
    if (Object.keys(tuitionAndFees).length > 0 && Object.keys(tuitionAndFees.tuition).length > 0) {
      // Convert planEndDate from string to Date, handling null/undefined/empty string cases
      const { detailedTuition } = tuitionAndFees.tuition as TuitionWithDetails;
      const planEndDate = parseValidDate(detailedTuition?.planEndDate);
      const planPayload = convertDataToPlan(tuitionAndFees.tuition, loggedInUser, student, planEndDate);
      await dispatch(saveOrganizationInvoicePlan(organization.id, planPayload));

      if (tuitionAndFees.tuition.proratedAmount && tuitionAndFees.tuition.proratedAmount > 0) {
        const proratePayload = convertDataToProrateInvoice(tuitionAndFees.tuition, loggedInUser, student);
        await dispatch(newOrganizationInvoice(organization.id, proratePayload));
      }
    }

    if (Object.keys(tuitionAndFees).length > 0 && tuitionAndFees.fees.length > 0) {
      for (const fee of tuitionAndFees.fees) {
        const invoicePayload = convertDataToInvoice(fee, loggedInUser, student);
        await dispatch(newOrganizationInvoice(organization.id, invoicePayload));
      }
    }
  };

  const onSubmit = async () => {
    if (!isSaving) {
      setIsSaving(true);
      try {
        await sendInvoices();
        await sendEnrollmentInvitations();
      } catch (error) {
        // A failed invoice-plan save now rejects instead of resolving silently;
        // let the provider see the failure and retry rather than leaving the
        // page saving forever with the invitations silently skipped.
        logError('Error submitting enrollment invoices', error);
        showToast(ToastTypeEnum.Error, t('enrollments.submissionFailed'));
        setIsSaving(false);
      }
    }
  };

  if (!student) return <LoadingComponent />;

  return (
    <>
      <Toast />
      <Segment loading={isSaving} basic clearing>
        <PageHeader pageName={'Enrollment'} classes="enrollments-page" />
        <h1 className="text-3xl" data-testid="dig-enroll-invitation">
          {t('enrollments.pageTitleSummary')}
        </h1>
        <Segment>
          <StudentView student={student} />
        </Segment>
        <Segment>
          <InvitationRecipientsView invitationRecipients={invitationRecipients} />
        </Segment>
        <Segment>
          <DocumentsView documents={documents} />
        </Segment>
        <Segment>
          <ScheduleView scheduledDays={scheduledDays} />
        </Segment>
        {Object.keys(tuitionAndFees).length > 0 && (
          <Segment>
            <EnrollmentInvoicesView
              tuition={tuitionAndFees.tuition}
              fees={tuitionAndFees.fees}
              enrollmentDate={student?.enrollmentDate}
            />
          </Segment>
        )}
        <Segment>
          <InvitationMessageView title={`[Recipient name], you're invited to enroll in ${organization.name}`} />
          {renderExtraNotesForm()}
        </Segment>
        <CommandButtons />
      </Segment>
      <EnrollmentInvitationConfirmationModal
        student={student}
        invitationRecipients={invitationRecipients}
        isOpen={isModalOpen}
        onClose={() => gotoRouteByName(RouteNameEnum.STUDENTS)}
      />
    </>
  );

  function CommandButtons() {
    return (
      <div className="ws-form-buttons" data-testid="es-ws-form-buttons">
        <Button
          primary
          loading={isSaving}
          onClick={() => {
            gotoRouteByName(RouteNameEnum.HOME);
          }}
          data-testid="es-cancel-btn"
          preIcon={<CloseIcon className="size-6" />}
        >
          {t('common.cancel')}
        </Button>
        <div className="flex gap-4">
          <Button onClick={goBack} data-testid="es-goback-btn" preIcon={<ArrowLeftIcon className="size-6" />}>
            {t('common.previous')}
          </Button>
          <Button
            primary
            loading={isSaving}
            onClick={onSubmit}
            data-testid="es-send-invite"
            postIcon={<ArrowRightIcon className="size-6" />}
          >
            {t('enrollments.enrollmentInfoSendEnrollmentButton')}
          </Button>
        </div>
      </div>
    );
  }

  function renderExtraNotesForm() {
    return (
      <Form>
        <p data-testid="extra-notes">
          <strong>{t('enrollments.enrollmentInfoExtraNotesLabel')}</strong>
        </p>
        <TextArea
          onChange={(e) => {
            if (e.target.value) setExtraNotes(e.target.value);
          }}
          data-testid="extra-notes-textarea"
        />
      </Form>
    );
  }
};

export default EnrollmentPage;
